import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { login as apiLogin, logout as apiLogout, onSessionExpired, setTokens, type Session } from '../api/client';

interface AuthValue {
  session: Session | null;
  signIn(tenantSlug: string, email: string, password: string): Promise<void>;
  signOut(): Promise<void>;
  can(permission: string): boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    onSessionExpired(() => setSession(null));
  }, []);

  const signIn = useCallback(async (tenantSlug: string, email: string, password: string) => {
    const next = await apiLogin(tenantSlug, email, password);
    setTokens(next);
    setSession(next);
  }, []);

  const signOut = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      setTokens(null);
      setSession(null);
    }
  }, []);

  const value = useMemo<AuthValue>(() => {
    const granted = new Set(session?.user.permissions ?? []);
    return {
      session,
      signIn,
      signOut,
      // Mirrors the server check so the interface does not offer actions that
      // will be refused. The server decides; this only shapes what is shown.
      can: (permission: string) => granted.has(permission),
    };
  }, [session, signIn, signOut]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
